import { forkJoin, iif, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { Select } from '@ngxs/store';

import { mergeApiRequests } from '@app/core/utilities/api.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { AccountGroupUserUpdate } from '../../settings/agency/account-group/states/update/account-group-update.models';
import {
  AccountApiRoute,
  ApiAccessLevel,
  ApiEndpoint,
  ApiGetListRequest,
  CustomerApiRoute,
} from '../models/api.models';
import { Account } from '../models/common.models';
import { Customer, CustomerUser } from '../models/customer.models';
import { CustomerState } from '../store/states/customer/customer.state';
import { ApiInstanceService } from './api-instance.service';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountGroupApiService extends ApiInstanceService {
  @Select(CustomerState.customerId)
  readonly customerId$: Observable<number>;

  readonly endpoint: ApiEndpoint = ApiEndpoint.Customers;

  constructor(api: ApiService) {
    super(api);
  }

  createAccountGroup(data: Partial<Customer>): Observable<number> {
    return this.post<Partial<Customer>>({
      data: { ...data, mainCustomerId: lastValue(this.customerId$) },
    }).pipe(map((response) => response.data?.customerId));
  }

  updateAccountGroup(customerId: number, data: Partial<Customer>, accessLevel?: ApiAccessLevel): Observable<void> {
    return this.patch({
      route: [customerId],
      data,
      accessLevel,
    }).pipe(map(() => void 0));
  }

  getAllAccounts(customerId: number, req?: Partial<ApiGetListRequest<Account>>): Observable<Account[]> {
    return this.getAll<Account>(
      mergeApiRequests(
        {
          endpoint: ApiEndpoint.Accounts,
          filterValues: { customerId },
        },
        req || {},
        true,
      ),
    );
  }

  getAccountById(adspertAccountId: number): Observable<Account> {
    return this.getObject<Account>({ endpoint: ApiEndpoint.Accounts, filterValues: { adspertAccountId } }).pipe(
      map((response) => response.data),
    );
  }

  getAllUsers(customerId: number, req?: Partial<ApiGetListRequest<CustomerUser>>): Observable<CustomerUser[]> {
    return this.getAll<CustomerUser>(
      mergeApiRequests(
        {
          route: [customerId, CustomerApiRoute.Access],
        },
        req || {},
        true,
      ),
    );
  }

  setAccountGroupUsers(customerId: number, users: AccountGroupUserUpdate[]): Observable<void> {
    const route = [customerId, CustomerApiRoute.Access];

    return this.getAll<CustomerUser>({
      route,
      include: ['userId', 'role', 'notifications'],
    }).pipe(
      switchMap((allUsers) => {
        const toPut = users
          .filter((user) => {
            const existingUser = allUsers.find(({ userId }) => userId === user.userId);

            return (
              !existingUser || existingUser.role !== user.role || existingUser.notifications !== user.notifications
            );
          })
          .map((user) => ({ userId: user.userId, notifications: !!user.notifications, role: user.role }));

        const toDelete = allUsers
          .filter((existingUser) => !users.some((user) => user.userId === existingUser.userId))
          .map((user) => ({ userId: user.userId }));

        return forkJoin([
          iif(() => !!toPut.length, this.put({ route, data: toPut }), of(void 0)),
          iif(() => !!toDelete.length, this.delete({ route, data: toDelete }), of(void 0)),
        ]).pipe(map(() => void 0));
      }),
    );
  }

  setAccountGroupAccounts(customerId: number, accounts: number[]): Observable<void> {
    const mainCustomerId = lastValue(this.customerId$);

    return this.getAll<Account>({
      endpoint: ApiEndpoint.Accounts,
      filterValues: { customerId },
      include: ['adspertAccountId'],
    }).pipe(
      switchMap((allAccounts) => {
        const accountIds = allAccounts.map(({ adspertAccountId }) => adspertAccountId);
        const toPut = accounts.filter((account) => !accountIds.includes(account));
        const toDelete = accountIds.filter((account) => !accounts.includes(account));

        const requests = [
          iif(
            () => !!toPut.length,
            this.post({
              endpoint: ApiEndpoint.Accounts,
              route: AccountApiRoute.Move,
              data: { customerId, adspertAccountIds: toPut },
            }),
            of(void 0),
          ),
          iif(
            () => !!toDelete.length,
            this.post({
              endpoint: ApiEndpoint.Accounts,
              route: AccountApiRoute.Move,
              data: { customerId: mainCustomerId, adspertAccountIds: toDelete },
            }),
            of(void 0),
          ),
        ];

        return forkJoin(requests).pipe(map(() => void 0));
      }),
    );
  }
}
